import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Plus, Edit, Trash2, Save, X, Truck, DollarSign } from 'lucide-react';
import { colors } from '../theme';

const TransportadoraManager = () => {
  const [transportadoras, setTransportadoras] = useState([]);
  const [selectedTransp, setSelectedTransp] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    nome: '',
    cnpj: '',
    telefone: '',
    email: '',
    url_rastreamento: '',
    ativa: true
  });
  
  const [tabelaFrete, setTabelaFrete] = useState([]);
  const [showTabelaForm, setShowTabelaForm] = useState(false);
  const [freteForm, setFreteForm] = useState({
    peso_min: '',
    peso_max: '',
    regiao_destino: 'Sul',
    valor: '',
    prazo_dias: ''
  });

  useEffect(() => {
    loadTransportadoras();
  }, []);

  const loadTransportadoras = async () => {
    try {
      const response = await axios.get(`${API}/transportadoras`);
      setTransportadoras(response.data);
    } catch (error) {
      console.error('Erro ao carregar transportadoras:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFreteInputChange = (e) => {
    const { name, value } = e.target;
    setFreteForm(prev => ({ ...prev, [name]: value }));
  };

  const selectTransportadora = (transp) => {
    setSelectedTransp(transp);
    setFormData({
      nome: transp.nome,
      cnpj: transp.cnpj || '',
      telefone: transp.telefone || '',
      email: transp.email || '',
      url_rastreamento: transp.url_rastreamento,
      ativa: transp.ativa
    });
    setTabelaFrete(transp.tabela_frete || []);
    setShowForm(false);
  };

  const novaTransportadora = () => {
    setSelectedTransp(null);
    setFormData({
      nome: '',
      cnpj: '',
      telefone: '',
      email: '',
      url_rastreamento: '',
      ativa: true
    });
    setTabelaFrete([]);
    setShowForm(true);
  };

  const saveTransportadora = async () => {
    setLoading(true);
    try {
      const data = {
        ...formData,
        tabela_frete: tabelaFrete
      };

      if (selectedTransp) {
        await axios.put(`${API}/transportadoras/${selectedTransp.id}`, data);
      } else {
        await axios.post(`${API}/transportadoras`, data);
      }

      alert('✅ Transportadora salva com sucesso!');
      loadTransportadoras();
      setShowForm(false);
      setSelectedTransp(null);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      alert('❌ Erro ao salvar transportadora');
    } finally {
      setLoading(false);
    }
  };

  const adicionarFaixaFrete = () => {
    const novaFaixa = {
      peso_min: parseFloat(freteForm.peso_min),
      peso_max: parseFloat(freteForm.peso_max),
      regiao_destino: freteForm.regiao_destino,
      valor: parseFloat(freteForm.valor),
      prazo_dias: parseInt(freteForm.prazo_dias)
    };

    setTabelaFrete([...tabelaFrete, novaFaixa]);
    setFreteForm({
      peso_min: '',
      peso_max: '',
      regiao_destino: 'Sul',
      valor: '',
      prazo_dias: ''
    });
    setShowTabelaForm(false);
  };

  const removerFaixaFrete = (index) => {
    setTabelaFrete(tabelaFrete.filter((_, i) => i !== index));
  };

  const regioes = ['Sul', 'Sudeste', 'Centro-Oeste', 'Nordeste', 'Norte'];

  return (
    <div className="space-y-6">
      {/* Lista de Transportadoras */}
      {!showForm && !selectedTransp && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span style={{ color: colors.primaza.secondary }}>
                🚚 Transportadoras Cadastradas ({transportadoras.length})
              </span>
              <Button
                onClick={novaTransportadora}
                style={{ backgroundColor: colors.primaza.primary, color: 'white' }}
              >
                <Plus size={16} className="mr-2" />
                Nova Transportadora
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {transportadoras.map((transp) => (
                <div
                  key={transp.id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => selectTransportadora(transp)}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-lg">{transp.nome}</h3>
                    <Badge
                      className={transp.ativa ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}
                    >
                      {transp.ativa ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600">
                    {transp.tabela_frete?.length || 0} faixas de frete cadastradas
                  </p>
                  {transp.email && (
                    <p className="text-xs text-gray-500 mt-1">📧 {transp.email}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Formulário Detalhado */}
      {(showForm || selectedTransp) && (
        <div className="space-y-6">
          {/* Botão Voltar */}
          <Button
            variant="outline"
            onClick={() => {
              setSelectedTransp(null);
              setShowForm(false);
            }}
          >
            <X size={16} className="mr-2" />
            Voltar
          </Button>

          {/* Dados da Transportadora */}
          <Card>
            <CardHeader>
              <CardTitle style={{ color: colors.primaza.secondary }}>
                {selectedTransp ? `Editar: ${selectedTransp.nome}` : 'Nova Transportadora'}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nome da Transportadora *</Label>
                  <Input
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    placeholder="EXPRESSO SÃO MIGUEL"
                  />
                </div>

                <div>
                  <Label>CNPJ</Label>
                  <Input
                    name="cnpj"
                    value={formData.cnpj}
                    onChange={handleInputChange}
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                <div>
                  <Label>Telefone</Label>
                  <Input
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleInputChange}
                    placeholder="(00) 0000-0000"
                  />
                </div>

                <div>
                  <Label>E-mail</Label>
                  <Input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="contato@transportadora.com.br"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>URL de Rastreamento</Label>
                  <Input
                    name="url_rastreamento"
                    value={formData.url_rastreamento}
                    onChange={handleInputChange}
                    placeholder="https://site.com/rastreamento?codigo={{code}}"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Use {'{{code}}'} para código e {'{{chave}}'} para chave NFe
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.ativa}
                  onChange={(e) => setFormData({ ...formData, ativa: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label>Transportadora Ativa</Label>
              </div>
            </CardContent>
          </Card>

          {/* Tabela de Frete */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span style={{ color: colors.primaza.secondary }}>
                  💰 Tabela de Frete ({tabelaFrete.length} faixas)
                </span>
                <Button
                  onClick={() => setShowTabelaForm(!showTabelaForm)}
                  variant="outline"
                  style={{ borderColor: colors.primaza.primary, color: colors.primaza.secondary }}
                >
                  <Plus size={16} className="mr-2" />
                  Adicionar Faixa
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Formulário de Nova Faixa */}
              {showTabelaForm && (
                <div className="p-4 border rounded-lg bg-gray-50 space-y-4">
                  <h4 className="font-medium">Nova Faixa de Frete</h4>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <Label className="text-xs">Peso Mín (kg)</Label>
                      <Input
                        name="peso_min"
                        type="number"
                        step="0.1"
                        value={freteForm.peso_min}
                        onChange={handleFreteInputChange}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Peso Máx (kg)</Label>
                      <Input
                        name="peso_max"
                        type="number"
                        step="0.1"
                        value={freteForm.peso_max}
                        onChange={handleFreteInputChange}
                        placeholder="1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Região</Label>
                      <select
                        name="regiao_destino"
                        value={freteForm.regiao_destino}
                        onChange={handleFreteInputChange}
                        className="w-full px-2 py-2 border rounded text-sm"
                      >
                        {regioes.map(r => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Valor (R$)</Label>
                      <Input
                        name="valor"
                        type="number"
                        step="0.01"
                        value={freteForm.valor}
                        onChange={handleFreteInputChange}
                        placeholder="15.00"
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Prazo (dias)</Label>
                      <Input
                        name="prazo_dias"
                        type="number"
                        value={freteForm.prazo_dias}
                        onChange={handleFreteInputChange}
                        placeholder="5"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={adicionarFaixaFrete}
                      style={{ backgroundColor: colors.primaza.primary, color: 'white' }}
                    >
                      Adicionar
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setShowTabelaForm(false)}
                    >
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* Tabela de Faixas */}
              {tabelaFrete.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left">Peso (kg)</th>
                        <th className="px-3 py-2 text-left">Região</th>
                        <th className="px-3 py-2 text-right">Valor</th>
                        <th className="px-3 py-2 text-center">Prazo</th>
                        <th className="px-3 py-2 text-center">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {tabelaFrete.map((faixa, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-2">
                            {faixa.peso_min} - {faixa.peso_max} kg
                          </td>
                          <td className="px-3 py-2">{faixa.regiao_destino}</td>
                          <td className="px-3 py-2 text-right">
                            R$ {faixa.valor.toFixed(2)}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {faixa.prazo_dias} dias
                          </td>
                          <td className="px-3 py-2 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => removerFaixaFrete(index)}
                              className="text-red-600"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Nenhuma faixa de frete cadastrada. Clique em "Adicionar Faixa" para começar.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tabela de Frete por Kg (Novo Modelo) */}
          {selectedTransp && selectedTransp.config_frete && selectedTransp.config_frete.tipo_precificacao === 'por_kg' && selectedTransp.regras_frete_kg && selectedTransp.regras_frete_kg.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle style={{ color: colors.primaza.secondary }}>
                  📊 Regras de Frete por Kg (Modelo Avançado)
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedTransp.config_frete.origem && `Origem: ${selectedTransp.config_frete.origem}`}
                  {selectedTransp.config_frete.validade_inicio && 
                    ` • Válida desde: ${new Date(selectedTransp.config_frete.validade_inicio).toLocaleDateString('pt-BR')}`}
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-3 py-2 text-left">Código</th>
                        <th className="px-3 py-2 text-left">Estado</th>
                        <th className="px-3 py-2 text-left">Descrição</th>
                        <th className="px-3 py-2 text-right">R$ por Kg</th>
                        <th className="px-3 py-2 text-right">Valor Mín.</th>
                        <th className="px-3 py-2 text-center">Prazo</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {selectedTransp.regras_frete_kg.map((regra, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-mono">{regra.codigo_destino}</td>
                          <td className="px-3 py-2">{regra.estado}</td>
                          <td className="px-3 py-2 text-gray-600">{regra.descricao}</td>
                          <td className="px-3 py-2 text-right font-medium">
                            R$ {regra.valor_por_kg.toFixed(3)}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {regra.valor_minimo ? `R$ ${regra.valor_minimo.toFixed(2)}` : '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {regra.prazo_dias || '-'} dias
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {selectedTransp.taxas_adicionais && selectedTransp.taxas_adicionais.length > 0 && (
                  <div className="mt-6 pt-6 border-t">
                    <h4 className="font-medium mb-3 text-gray-700">💰 Taxas Adicionais Configuradas:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {selectedTransp.taxas_adicionais.filter(t => t.ativa).map((taxa, index) => (
                        <div key={index} className="p-3 bg-gray-50 rounded border">
                          <div className="font-medium text-sm">{taxa.tipo}</div>
                          <div className="text-xs text-gray-600 mt-1">{taxa.descricao}</div>
                          {taxa.condicao && (
                            <div className="text-xs text-gray-500 mt-1 italic">{taxa.condicao}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
                  <strong>ℹ️ Informação:</strong> Esta transportadora usa o modelo de precificação por Kg. 
                  A edição destas regras será disponibilizada em breve.
                </div>
              </CardContent>
            </Card>
          )}

          {/* Botão Salvar */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => {
                setSelectedTransp(null);
                setShowForm(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={saveTransportadora}
              disabled={loading || !formData.nome}
              style={{ backgroundColor: colors.primaza.primary, color: 'white' }}
            >
              {loading ? 'Salvando...' : (
                <>
                  <Save size={16} className="mr-2" />
                  Salvar Transportadora
                </>
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportadoraManager;
